<template>
  <v-container id="Invoice-items">
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="items"
      dense
      :hide-default-footer="true"
      :disable-sort="true"
      :hide-default-header="true"
    >
      <!-- Custom Header -->
      <template v-slot:header="{ props }" v-if="items.length > 0">
        <thead>
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              class="custom-header"
              style=" border-bottom: thin solid rgb(0 0 0 / 0%);"
            >
              <p>{{ header.text }}</p>
            </th>
          </tr>
        </thead>
      </template>

      <!-- Body Rows -->
      <template v-slot:body="{ items }">
        <tr v-for="(item, index) in items" :key="index">
          <td class="pa-1">
            <v-autocomplete
              v-model="item.id"
              :items="products"
              :item-text="
                (product) => `${product.product_code} - ${product.name}`
              "
              item-value="id"
              dense
              outlined
              clearable
              placeholder="Product Name"
              :rules="[(v) => !!v || 'Product is required']"
              @change="fetchBatches(item.id, index)"
            ></v-autocomplete>
          </td>

          <td class="pa-1">
            <v-autocomplete
              v-model="item.batch_id"
              :items="itemBatches[index] || []"
              item-title="batch_code"
              item-value="batch_id"
              dense
              outlined
              clearable
              placeholder="Select Batch"
              :loading="loadingBatches"
              no-data-text="First Select a Product and branch"
              :rules="[(v) => !!v || 'Batch is required']"
              @change="fetchpricetaxdiscount(item.id, item.batch_id, index)"
            >
              <template v-slot:item="{ item }">
                <span>
                  {{ item.batch_code }} (Exp: {{ item.expiry_date || "N/A" }}) -
                  Stock: {{ item.stock_in_hand_quantity || 0 }}
                </span>
              </template>
              <template v-slot:selection="{ item }">
                <span>{{ item.batch_code }}</span>
              </template>
            </v-autocomplete>
          </td>

          <td class="wider-description" v-if="false">
            <v-text-field
              v-model="item.description"
              placeholder="Description"
              dense
              outlined
              clearable
            ></v-text-field>
          </td>
          <td class="pa-1">
            <v-text-field
              v-model.number="item.quantity"
              placeholder="Quantity"
              type="number"
              dense
              outlined
              :min="1"
              clearable
              :rules="[
                (v) => !!v || 'Quantity is required',
                (v) => v >= 1 || 'Min 1',
                (v) => (!isNaN(v) && v <= 999999) || 'Max 999999',
              ]"
              @input="validateQuantity(item, index)"
            ></v-text-field>
          </td>
          <td class="pa-1">
            <v-text-field
              v-model.number="item.unit_price"
              placeholder="Unit Price"
              type="number"
              dense
              outlined
              readonl
            ></v-text-field>
          </td>
          <td class="pa-1">
            <v-text-field
              v-model="item.discount"
              placeholder="Discount (%)"
              type="number"
              dense
              :min="0"
              outlined
              :rules="[
                (v) =>
                  v === '' || v === null || (!isNaN(v) && v >= 0) || 'Min 0',
                (v) =>
                  v === '' ||
                  v === null ||
                  (!isNaN(v) && v <= 100) ||
                  'Max 100',
              ]"
            ></v-text-field>
          </td>
          <td class="pa-1">
            <v-text-field
              v-model="item.tax_percent"
              placeholder="VAT (%)"
              type="number"
              dense
              outlined
              :rules="[
                (v) =>
                  v === '' || v === null || (!isNaN(v) && v >= 0) || 'Min 0',
                (v) =>
                  v === '' ||
                  v === null ||
                  (!isNaN(v) && v <= 100) ||
                  'Max 100',
              ]"
            ></v-text-field>
          </td>
          <td class="pa-1">
            <v-text-field
              :value="formatCurrency(calculateTax(item))"
              placeholder="TAX (Rs.)"
              type="number"
              dense
              outlined
              readonly
            ></v-text-field>
          </td>
          <td class="pa-1">
            <v-text-field
              :value="formatCurrency(calculateEffectivePrice(item))"
              placeholder="Total (Rs.)"
              dense
              outlined
              readonly
            ></v-text-field>
          </td>
          <td class="pb-5">
            <v-icon
              size="30"
              color="red"
              @click="$emit('remove-item', index)"
              class="delete-icon"
            >
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-row class="mt-0" v-if="items.length < 10">
      <v-col class="text-left">
        <v-icon size="40" color="blue" @click="$emit('add-item')"
          >mdi-plus</v-icon
        >
        <span>Add Item</span>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { InvoiceService } from "@/services/invoice-service.js";
import { checkError } from "@/config/errors.js";
import Alert from "@/components/shared/Alert";
import { numeric } from "vuelidate/lib/validators";

export default {
  name: "ItemsTable",
  components: { Alert },
  props: {
    priceType: {
      type: String,
      default: "retail",
    },
    items: { type: Array, required: true },
    branch_id: { type: numeric, required: true },
    store_id: { type: numeric, required: true },
    products: { type: Array, required: true },
    headers: {
      type: Array,
      default: () => [
        { text: "Product", value: "product_id" },
        { text: "Batch", value: "batch_id" },
        //{ text: "Description", value: "description" },
        { text: "Quantity", value: "quantity" },
        { text: "Unit Price", value: "unit_price" },
        { text: "Discount", value: "discount" },
        { text: "Tax (%)", value: "tax_percent" },
        { text: "Tax Amount", value: "tax" },
        { text: "Total Price", value: "effective_price" },
        { text: "", value: "actions", sortable: false },
      ],
    },
    title: { type: String, default: "Items" },
  },
  data() {
    return {
      itemBatches: {},
      loadingBatches: false,
      errors: null,
    };
  },
  mounted() {
    if (Array.isArray(this.items) && this.items.length > 0) {
      this.preloadItemBatches();
    }
  },
  watch: {
    "items.length"(newLength, oldLength) {
      if (newLength > oldLength) {
        this.preloadItemBatches();
      }
    },
  },

  methods: {
    itemClicked(clickedItem, index) {
      this.items[index].batch_id = clickedItem.batch_id;
    },
    calculateEffectivePrice(item) {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const taxPercent = parseFloat(item.tax_percent) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discountPercent = parseFloat(item.discount) || 0;

      const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
      const taxAmount = (discountedPrice * taxPercent) / 100;
      return (discountedPrice + taxAmount) * quantity;
    },

    calculateTax(item) {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const taxPercent = parseFloat(item.tax_percent) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discountPercent = parseFloat(item.discount) || 0;

      const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
      return (discountedPrice * taxPercent * quantity) / 100;
    },

    validateQuantity(item, index) {
      const selectedBatch = this.itemBatches[index]?.find(
        (batch) => batch.batch_id === item.batch_id
      );
      if (selectedBatch) {
        const maxStock = selectedBatch.stock_in_hand_quantity || 0;
        if (item.quantity > maxStock) {
          this.$set(this.items, index, {
            ...item,
            quantity: maxStock,
          });
          let data = { message: `Maximum stock available: ${maxStock}` };
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        }
      }
    },

    async preloadItemBatches() {
      if (!Array.isArray(this.items)) return;

      for (let index = 0; index < this.items.length; index++) {
        const item = this.items[index];
        if (item && item.id) {
          await this.fetchBatches(item.id, index);
          if (item.batch_id) {
            await this.fetchpricetaxdiscount(item.id, item.batch_id, index);
          }
        }
      }
    },
    async fetchBatches(productId, index) {
      if (!productId) return;
      if (!this.branch_id || !this.store_id) {
        let data = { message: "Please select a branch and store first" };
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
        return;
      }

      this.loadingBatches = true;

      try {
        const [batchResponse] = await Promise.all([
          InvoiceService.getBatches(productId, this.branch_id, this.store_id),
        ]);

        this.$set(
          this.itemBatches,
          index,
          Array.isArray(batchResponse) ? [...batchResponse] : []
        );
      } catch (error) {
        console.error("Error fetching Batches:", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loadingBatches = false;
      }
    },

    async fetchpricetaxdiscount(productId, batchId, index) {
      if (!productId || !batchId) return;
      try {
        const pricingResponse = await InvoiceService.getProductPricing(
          productId,
          batchId
        );
        if (pricingResponse) {
          this.$set(this.items, index, {
            ...this.items[index],
            unit_price:
              this.priceType === "retail"
                ? pricingResponse.price || 0
                : pricingResponse.wholesale || 0,
            unit_retail_price: pricingResponse.price || 0,
            unit_wholesale_price: pricingResponse.wholesale || 0,
            tax_percent: pricingResponse.tax || 0,
            discount: pricingResponse.discount || 0,
            cost: pricingResponse.cost || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching pricing details:", error);
        let data = error.response?.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      }
    },

    formatCurrency(value) {
      const num = Number(value);
      return isNaN(num) ? "0.00" : num.toFixed(2);
    },

    onDismissed() {
      this.errors = null;
    },
  },
};
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: rgba(192, 0, 250, 0.215);
  border-radius: 10px;
}
</style>
